import { isTier } from './features.js';
/**
 * The rates Rythmo's **transaction fee** defaults to, per tier
 * ([ADR-0010](../../../docs/decisions/0010-how-rythmo-collects-revenue.md), amended by
 * [ADR-0042](../../../docs/decisions/0042-a-schools-transaction-fee-is-its-tier-rate-unless-negotiated.md)).
 *
 * **Percent, not a fraction.** A recurring membership can only carry the fee as
 * Stripe's `application_fee_percent`, which takes a percent with at most two
 * decimals — so every rate this system holds is one Stripe will accept, and a
 * pass and a subscription round from the same number.
 */
export const FEE_PERCENT_BY_TIER = Object.freeze({
    starter: 1.5,
    pulse: 1,
    groove: 0.5,
    studio: 0,
    custom: 0,
});
/**
 * The school's **effective** rate: a rate an Operator negotiated with it, or its
 * tier's rate when there is none. This is the only place the two are combined.
 *
 * A negotiated `0` is a rate, not "unset". An unrecognised tier resolves to the
 * starter rate, following `resolveBundle`'s precedent: falling back to 0% would
 * make a corrupt tier value silently free.
 */
export function transactionFeePercent(tier, negotiatedPercent) {
    if (negotiatedPercent !== null)
        return negotiatedPercent;
    return isTier(tier) ? FEE_PERCENT_BY_TIER[tier] : FEE_PERCENT_BY_TIER.starter;
}
/**
 * The fee on one charge, in minor units, **rounded half-up** — which is what
 * Stripe does for `application_fee_percent` (confirmed against the sandbox,
 * ADR-0042), so a pass and a subscription on the same total take the same fee.
 *
 * **Whole basis points, never floating point.** 0.29% of 25000 is exactly 72.5;
 * `Math.round(25000 * 0.29 / 100)` gives 72, because the product is
 * 72.49999999999999. With Operator-negotiated rates any two-decimal percent is
 * reachable, so the float path would under-collect on real charges.
 */
export function transactionFeeMinor(percent, amountMinor) {
    const basisPoints = Math.round(percent * 100);
    return Math.floor((amountMinor * basisPoints + 5000) / 10000);
}
//# sourceMappingURL=billing.js.map